import { Glyph, type GlyphName } from '@/components/svgs/Glyph'
import { colors } from '@/constants/colors'
import { Turning } from '@/ui/core/motion'
import { Pressable } from '@/ui/core/pressable'
import { HStack, VStack } from '@/ui/core/stack'
import { Text } from '@/ui/core/typography'
import type { ReactNode } from 'react'
import { View } from 'react-native'

/**
 * A sheet's title with its glyph on a pink disc: the same mark and the same face as its row on
 * the profile, one size up. The nudge puts the capitals, not the box, on the disc's centre.
 * When the sheet has stepped into a detail, the disc is the way back instead. The title owns the
 * air around it: 16pt in from the sheet's edge, so its disc lines up with the row discs on the
 * page beneath (page inset plus group inset), a beat down from the grabber, and `mb-11`
 * of air below, so every sheet breathes the same before its content starts.
 *
 * That is 38.5pt, not the 44 this comment used to claim: this Tailwind's rem is fourteen, so
 * 2.75rem is 38.5. **The air is the title's own, and a sheet must not add to it.** The payments,
 * plans, forms and visits sheets each wrapped this in a `VStack gap={20}`, which put another
 * twenty on top and left them visibly looser than the membership sheet; the results sheet pulled
 * twelve back off. Keep `SheetTitle` out of a gapped stack and every sheet agrees. `turning` sets the glyph
 * revolving, for a sheet whose subject is a thing not yet done.
 *
 * `line` sets a second line under the name, in the face a row uses for its own: the sheet is
 * often the thing a row opened, and it should say what that row said, one size up.
 *
 * `trailing` puts something at the row's end, opposite the mark — the arrival guide's position
 * dots, which belong beside the name of the thing they are counting rather than under the
 * photograph they are counting through.
 */
export const SheetTitle = ({
	glyph,
	onBack,
	turning = false,
	line,
	trailing,
	children,
}: {
	glyph: GlyphName
	onBack?: () => void
	turning?: boolean
	line?: string
	trailing?: ReactNode
	children: string
}) => (
	<HStack gap={12} className="items-center px-4 mt-4 mb-11" accessibilityRole="header">
		{onBack ? (
			<Pressable
				onPress={onBack}
				accessibilityRole="button"
				accessibilityLabel="Back"
				hitSlop={8}
				className="h-10 w-10 items-center justify-center rounded-full bg-ground"
			>
				<Glyph name="arrow" size={18} color={colors.ink} rotate={180} />
			</Pressable>
		) : (
			<View className="h-10 w-10 items-center justify-center rounded-full bg-ground">
				{turning ? (
					<Turning>
						<Glyph name={glyph} size={20} color={colors.ink} />
					</Turning>
				) : (
					<Glyph name={glyph} size={20} color={colors.ink} />
				)}
			</View>
		)}
		<VStack gap={0} className="flex-1">
			<Text
				weight="bold"
				// A line under the name takes the leading in with it, so the two read as one block.
				className={line ? 'text-[24px] leading-[25px]' : 'text-[24px] leading-[28px]'}
				// The nudge puts the capitals on the disc's centre. With a line under them the pair is
				// centred as a block, and the nudge would tip it.
				style={line ? undefined : { marginTop: 3 }}
			>
				{children}
			</Text>
			{!!line && (
				<Text size="body1" tone="soft">
					{line}
				</Text>
			)}
		</VStack>
		{trailing}
	</HStack>
)
